import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalProfile, mapCustomerToProfile } from '../../contexts/PortalProfileContext';
import { get, put, post, patch, del } from '../../utils/api';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { usePortalPoshaka } from '../../contexts/PortalPoshakaContext';
import {
  US_STATES,
  DetailRow,
  CloseX,
  formatRelationLabel,
  formatOccasionTypeLabel,
  formatOccasionMonthDay,
  RELATION_OPTIONS,
  OCCASION_TYPES,
  MONTHS,
  DAYS,
  formatMonthSelectLabel,
  ignoreBackdropEscapeClose,
} from '../../components/portal/portalUi';

const POSHAKA_ARCHANA_OCCASION_BENEFIT =
  'Special Archana and blessings on any 3 dates [birthdays, anniversary etc.] (in person)';

/** Primary (self) row: server flag and/or relation (legacy rows may lack is_primary). */
function isPrimaryFamilyMember(m) {
  if (!m) return false;
  if (m.is_primary) return true;
  return String(m.relation || '').toLowerCase() === 'self';
}

/** Legacy DB value "spouse" maps into the new wife/husband picker (user can correct). */
function normalizeRelationForForm(rel) {
  const r = String(rel || 'self').toLowerCase();
  if (RELATION_OPTIONS.includes(r)) return r;
  if (r === 'spouse') return 'wife';
  return 'other';
}

export default function PortalProfilePage() {
  const { staff } = useAuth();
  const { profile, setProfile } = usePortalProfile();
  const { poshaka } = usePortalPoshaka();

  const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Devotee Name';
  const email = profile.email || 'email@domain.com';
  const phone = profile.phone || '(000) 000-0000';
  const address1 = profile.billing?.address_1 || '';
  const address2 = profile.billing?.address_2 || '';
  const city = profile.billing?.city || '';
  const state = profile.billing?.state || '';
  const zip = profile.billing?.postcode || '';
  const country = profile.billing?.country || '';
  const addrLine1 = [address1, address2].filter(Boolean).join(', ');
  const addrLine2 = `${city}${city && state ? ', ' : ''}${state} ${zip}`.trim();
  const fullAddress = [addrLine1, addrLine2, country].filter(Boolean).join('\n');

  const [familyMembers, setFamilyMembers] = React.useState([]);
  const [familyLoading, setFamilyLoading] = React.useState(false);
  const [occasions, setOccasions] = React.useState([]);
  const [occasionsLoading, setOccasionsLoading] = React.useState(false);
  const [occasionsError, setOccasionsError] = React.useState('');

  const [famDialogOpen, setFamDialogOpen] = React.useState(false);
  const [famEditingId, setFamEditingId] = React.useState(null);
  const [famSaving, setFamSaving] = React.useState(false);
  const [famFormError, setFamFormError] = React.useState('');
  const [famSuccess, setFamSuccess] = React.useState('');
  const [famForm, setFamForm] = React.useState({ name: '', relation: 'self' });

  const [occDialogOpen, setOccDialogOpen] = React.useState(false);
  const [occEditing, setOccEditing] = React.useState(null);
  const [occSaving, setOccSaving] = React.useState(false);
  const [occFormError, setOccFormError] = React.useState('');
  const [occSuccess, setOccSuccess] = React.useState('');
  const [occForm, setOccForm] = React.useState({
    family_member_id: '',
    occasion_type: 'birthday',
    occasion_label: '',
    day: 1,
    month: 1,
  });

  const [occasionEmailReminders, setOccasionEmailReminders] = React.useState(true);
  const [prefsLoading, setPrefsLoading] = React.useState(false);
  const [prefsSaving, setPrefsSaving] = React.useState(false);
  const [prefsSaveError, setPrefsSaveError] = React.useState('');

  const [editOpen, setEditOpen] = React.useState(false);
  const [editLoading, setEditLoading] = React.useState(false);
  const [editError, setEditError] = React.useState('');
  const [editSuccess, setEditSuccess] = React.useState('');
  const [editForm, setEditForm] = React.useState({
    first_name: '', last_name: '', phone: '', spouse: '', gothram: '',
    address_1: '', address_2: '', city: '', state: '', postcode: '', country: 'US',
  });

  const loadFamilyAndOccasions = React.useCallback(async () => {
    if (!staff?.subject) return;
    try {
      setFamilyLoading(true);
      const famRes = await get('/portal/devotees/family-members');
      const famList = Array.isArray(famRes?.data) ? famRes.data : [];
      setFamilyMembers(famList);
    } catch (e) {
      console.warn('[PortalProfile][Family]', e?.message || e);
      setFamilyMembers([]);
    } finally {
      setFamilyLoading(false);
    }
    try {
      setOccasionsLoading(true);
      setOccasionsError('');
      const occRes = await get('/portal/devotees/family-occasions');
      const membersWithOcc = Array.isArray(occRes?.data) ? occRes.data : [];
      const flat = [];
      membersWithOcc.forEach((m) => {
        (m.occasions || []).forEach((o) => {
          flat.push({
            ...o,
            family_member_id: o.family_member_id ?? m.id,
            member: { id: m.id, name: m.name, relation: m.relation },
          });
        });
      });
      setOccasions(flat);
    } catch (e) {
      console.error('[PortalProfile][Occasions]', e);
      setOccasionsError(e?.message || 'Failed to load occasions.');
      setOccasions([]);
    } finally {
      setOccasionsLoading(false);
    }
  }, [staff?.subject]);

  const loadPortalPreferences = React.useCallback(async () => {
    setPrefsSaveError('');
    setPrefsLoading(true);
    try {
      const res = await get('/portal/me/preferences');
      const d = res?.data;
      if (d && typeof d.occasionEmailReminders === 'boolean') {
        setOccasionEmailReminders(d.occasionEmailReminders);
      }
    } catch (e) {
      setPrefsSaveError(e?.message || 'Could not load preferences.');
    } finally {
      setPrefsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (poshaka.isSubscriber) loadPortalPreferences();
  }, [poshaka.isSubscriber, loadPortalPreferences]);

  React.useEffect(() => {
    if (staff?.subject) loadFamilyAndOccasions();
  }, [staff?.subject, loadFamilyAndOccasions]);

  const openFamAdd = () => {
    setFamEditingId(null);
    setFamFormError('');
    setFamSuccess('');
    setFamForm({ name: '', relation: 'wife' });
    setFamDialogOpen(true);
  };

  const openFamEdit = (m) => {
    setFamEditingId(m.id);
    setFamFormError('');
    setFamSuccess('');
    setFamForm({
      name: m.name || '',
      relation: normalizeRelationForForm(m.relation),
    });
    setFamDialogOpen(true);
  };

  const saveFamilyMember = async () => {
    setFamFormError('');
    const nameTrim = String(famForm.name || '').trim();
    const rel = String(famForm.relation || '').trim();
    if (!nameTrim) {
      setFamFormError('Name is required.');
      return;
    }
    if (!rel) {
      setFamFormError('Relation is required.');
      return;
    }
    setFamSaving(true);
    setFamSuccess('');
    try {
      if (famEditingId) {
        await put(`/portal/devotees/family-members/${famEditingId}`, {
          name: nameTrim,
          relation: rel,
          gender: '',
        });
      } else {
        await post('/portal/devotees/family-members', {
          name: nameTrim,
          relation: rel,
          gender: '',
        });
      }
      setFamSuccess(famEditingId ? 'Family member updated.' : 'Family member added.');
      setFamFormError('');
      await loadFamilyAndOccasions();
    } catch (e) {
      setFamSuccess('');
      setFamFormError(e?.message || 'Save failed');
    } finally {
      setFamSaving(false);
    }
  };

  const removeFamilyMember = async (m) => {
    if (isPrimaryFamilyMember(m)) return;
    if (!window.confirm(`Remove ${m.name || 'this member'} from your family list?`)) return;
    try {
      await del(`/portal/devotees/family-members/${m.id}`);
      await loadFamilyAndOccasions();
    } catch (e) {
      window.alert(e?.message || 'Could not delete');
    }
  };

  const openOccAdd = () => {
    if (familyMembers.length === 0) {
      window.alert('Add at least one family member before adding occasions.');
      return;
    }
    setOccEditing(null);
    setOccFormError('');
    setOccSuccess('');
    const first = familyMembers[0];
    setOccForm({
      family_member_id: String(first.id),
      occasion_type: 'birthday',
      occasion_label: '',
      day: 1,
      month: 1,
    });
    setOccDialogOpen(true);
  };

  const openOccEdit = (o) => {
    setOccEditing(o);
    setOccFormError('');
    setOccSuccess('');
    setOccForm({
      family_member_id: String(o.family_member_id),
      occasion_type: o.occasion_type || 'birthday',
      occasion_label: o.occasion_label || '',
      day: Number(o.day) || 1,
      month: Number(o.month) || 1,
    });
    setOccDialogOpen(true);
  };

  const saveOccasion = async () => {
    setOccFormError('');
    const mid = parseInt(occForm.family_member_id, 10);
    if (!mid) {
      setOccFormError('Choose a family member.');
      return;
    }
    setOccSaving(true);
    setOccSuccess('');
    try {
      const body = {
        occasion_type: occForm.occasion_type,
        occasion_label: String(occForm.occasion_label || '').trim(),
        day: Number(occForm.day),
        month: Number(occForm.month),
      };
      if (occEditing) {
        await put(
          `/portal/devotees/family-members/${occEditing.family_member_id}/occasions/${occEditing.id}`,
          body
        );
      } else {
        await post(`/portal/devotees/family-members/${mid}/occasions`, body);
      }
      setOccSuccess(occEditing ? 'Occasion updated.' : 'Occasion added.');
      setOccFormError('');
      await loadFamilyAndOccasions();
    } catch (e) {
      setOccSuccess('');
      setOccFormError(e?.message || 'Save failed');
    } finally {
      setOccSaving(false);
    }
  };

  const removeOccasion = async (o) => {
    if (!window.confirm('Remove this occasion?')) return;
    try {
      await del(`/portal/devotees/family-members/${o.family_member_id}/occasions/${o.id}`);
      await loadFamilyAndOccasions();
    } catch (e) {
      window.alert(e?.message || 'Could not delete');
    }
  };

  const openEdit = React.useCallback(() => {
    setEditError('');
    setEditSuccess('');
    setEditForm({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      phone: profile.phone || '',
      spouse: profile.meta?.spouse || '',
      gothram: profile.meta?.gothram || '',
      address_1: profile.billing?.address_1 || '',
      address_2: profile.billing?.address_2 || '',
      city: profile.billing?.city || '',
      state: profile.billing?.state || '',
      postcode: profile.billing?.postcode || '',
      country: profile.billing?.country || 'US',
    });
    setEditOpen(true);
  }, [profile]);

  React.useEffect(() => {
    let cancelled = false;
    const loadCustomer = async () => {
      if (!editOpen) return;
      try {
        setEditLoading(true);
        setEditError('');
        const data = await get('/portal/me/customer');
        if (cancelled) return;
        const billing = {
          address_1: data.billing_address_1 || '',
          address_2: data.billing_address_2 || '',
          city: data.billing_city || '',
          state: data.billing_state || '',
          postcode: data.billing_postcode || '',
          country: data.billing_country || 'US',
          phone: data.billing_phone || data.phone || '',
        };
        const meta = data.meta_data || {};
        setEditForm({
          first_name: data.first_name || data.billing_first_name || '',
          last_name: data.last_name || data.billing_last_name || '',
          phone: billing.phone || '',
          spouse: meta.billing_spouse || '',
          gothram: meta.billing_gothram || '',
          address_1: billing.address_1,
          address_2: billing.address_2,
          city: billing.city,
          state: billing.state,
          postcode: billing.postcode,
          country: billing.country,
        });
        setProfile(mapCustomerToProfile(data));
      } catch (e) {
        if (!cancelled) setEditError(e?.message || 'Failed to load customer');
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    };
    loadCustomer();
    return () => { cancelled = true; };
  }, [editOpen, setProfile]);

  const handleEditChange = (field) => (e) => setEditForm((f) => ({ ...f, [field]: e.target.value }));
  const handleAddressChange = (field) => (e) => setEditForm((f) => ({ ...f, [field]: e.target.value }));

  const handleEditSave = async () => {
    try {
      setEditLoading(true);
      setEditError('');
      setEditSuccess('');
      const payload = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        phone: editForm.phone,
        billing: {
          address_1: editForm.address_1,
          address_2: editForm.address_2,
          city: editForm.city,
          state: editForm.state,
          postcode: editForm.postcode,
          country: editForm.country,
          phone: editForm.phone,
        },
        spouse: editForm.spouse,
        gothram: editForm.gothram,
      };
      const updated = await put('/portal/me/customer', payload);
      if (updated && typeof updated === 'object') {
        setProfile((prev) => ({
          ...prev,
          subject: updated.subject ?? prev.subject,
          first_name: updated.first_name ?? prev.first_name,
          last_name: updated.last_name ?? prev.last_name,
          email: updated.email ?? prev.email,
          phone: updated.phone ?? prev.phone,
          billing: {
            ...prev.billing,
            address_1: updated.billing?.address_1 ?? prev.billing.address_1,
            address_2: updated.billing?.address_2 ?? prev.billing.address_2,
            city: updated.billing?.city ?? prev.billing.city,
            state: updated.billing?.state ?? prev.billing.state,
            postcode: updated.billing?.postcode ?? prev.billing.postcode,
            country: updated.billing?.country ?? prev.billing.country,
          },
          meta: {
            ...prev.meta,
            spouse: updated.meta?.spouse ?? prev.meta.spouse,
            gothram: updated.meta?.gothram ?? prev.meta.gothram,
            family_members: Array.isArray(updated.meta?.family_members)
              ? updated.meta.family_members
              : (prev.meta.family_members || []),
          },
        }));
      }
      setEditSuccess('Your profile was saved.');
      setEditError('');
    } catch (e) {
      setEditSuccess('');
      setEditError(e?.message || 'Save failed');
    } finally {
      setEditLoading(false);
    }
  };

  const onOccasionRemindersChange = async (_e, checked) => {
    setPrefsSaveError('');
    const prev = occasionEmailReminders;
    setOccasionEmailReminders(checked);
    setPrefsSaving(true);
    try {
      await patch('/portal/me/preferences', { occasionEmailReminders: checked });
    } catch (e) {
      setOccasionEmailReminders(prev);
      setPrefsSaveError(e?.message || 'Could not save preference.');
    } finally {
      setPrefsSaving(false);
    }
  };

  const atFamilyLimit = familyMembers.length >= 10;

  const closeFamDialog = React.useCallback(() => {
    if (famSaving) return;
    setFamDialogOpen(false);
    setFamSuccess('');
    setFamFormError('');
  }, [famSaving]);

  const closeOccDialog = React.useCallback(() => {
    if (occSaving) return;
    setOccDialogOpen(false);
    setOccSuccess('');
    setOccFormError('');
  }, [occSaving]);

  const closeEditDialog = React.useCallback(() => {
    setEditOpen(false);
    setEditError('');
    setEditSuccess('');
  }, []);

  return (
    <>
      <PortalPageHeader title="My profile" />

      <Grid container spacing={2} direction="column">
        <Grid item xs={12}>
          <Box>
              <Card variant="outlined" sx={{ width: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                    <Button size="small" variant="contained" onClick={openEdit}>Edit profile</Button>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                      gap: { xs: 2, md: 3 },
                      alignItems: 'start',
                    }}
                  >
                    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                      <DetailRow label="Name" value={name} editable={false} />
                      <DetailRow label="Email" value={email} editable={false} />
                      <DetailRow label="Phone" value={phone} editable={false} />
                    </Stack>
                    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                      <DetailRow label="Spouse" value={profile.meta?.spouse || '—'} editable={false} />
                      <DetailRow label="Gothram" value={profile.meta?.gothram || '—'} editable={false} />
                    </Stack>
                    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                      <DetailRow
                        label="Address"
                        value={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{fullAddress}</Box>}
                        editable={false}
                      />
                    </Stack>
                  </Box>
                </CardContent>
              </Card>

              <Box
                sx={{
                  mt: 3,
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                  '& > *': { minWidth: 0 },
                }}
              >
                <Card variant="outlined" sx={{ height: '100%', minWidth: 0 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Family Members</Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openFamAdd}
                        disabled={atFamilyLimit || familyLoading}
                      >
                        Add
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Up to 10 members.
                    </Typography>
                    {familyLoading ? (
                      <Typography variant="body2" color="text.secondary">Loading family members…</Typography>
                    ) : (
                      <Stack spacing={0}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr minmax(72px,auto) minmax(88px,auto)', gap: 1, alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
                          <Typography variant="caption" color="text.secondary">Name</Typography>
                          <Typography variant="caption" color="text.secondary">Relation</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}> </Typography>
                        </Box>
                        {familyMembers.length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No family members yet.</Typography>
                        )}
                        {familyMembers.map((m) => (
                          <Box
                            key={m.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr minmax(72px,auto) minmax(88px,auto)',
                              gap: 1,
                              alignItems: 'center',
                              py: 0.5,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: isPrimaryFamilyMember(m) ? 700 : 500 }}>
                              {m.name || 'Unnamed'}
                              {isPrimaryFamilyMember(m) ? ' (Self)' : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{formatRelationLabel(m.relation)}</Typography>
                            <Box sx={{ textAlign: 'right' }}>
                              <IconButton size="small" aria-label="Edit member" onClick={() => openFamEdit(m)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {!isPrimaryFamilyMember(m) && (
                                <IconButton size="small" aria-label="Remove member" color="error" onClick={() => removeFamilyMember(m)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ height: '100%', minWidth: 0 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Important Occasions</Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openOccAdd}
                        disabled={occasionsLoading || familyMembers.length === 0}
                      >
                        Add
                      </Button>
                    </Box>
                    {occasionsError && (<Typography variant="body2" color="error" sx={{ mb: 1 }}>{occasionsError}</Typography>)}
                    {occasionsLoading ? (
                      <Typography variant="body2" color="text.secondary">Loading occasions…</Typography>
                    ) : (
                      <Stack spacing={0}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto minmax(88px,auto)', gap: 1, alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
                          <Typography variant="caption" color="text.secondary">Member</Typography>
                          <Typography variant="caption" color="text.secondary">Occasion</Typography>
                          <Typography variant="caption" color="text.secondary">Date</Typography>
                          <span />
                        </Box>
                        {occasions.length === 0 && !occasionsError && (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No important occasions yet.</Typography>
                        )}
                        {occasions.map((o) => (
                          <Box
                            key={o.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr auto minmax(88px,auto)',
                              gap: 1,
                              alignItems: 'center',
                              py: 0.5,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{o.member?.name || 'Unknown'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatOccasionTypeLabel(o.occasion_type, o.occasion_label)}
                            </Typography>
                            <Typography variant="body2">
                              {formatOccasionMonthDay(o.month, o.day)}
                            </Typography>
                            <Box sx={{ textAlign: 'right' }}>
                              <IconButton size="small" aria-label="Edit occasion" onClick={() => openOccEdit(o)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" aria-label="Delete occasion" color="error" onClick={() => removeOccasion(o)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                    {poshaka.isSubscriber && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Poshaka benefit (occasions)
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ pl: 1, mb: 1.5 }}>
                          <Box component="span" sx={{ display: 'block' }}>
                            • {POSHAKA_ARCHANA_OCCASION_BENEFIT}
                          </Box>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          If you keep email reminders on, we will email you about one week before each important occasion.
                        </Typography>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={occasionEmailReminders}
                              onChange={onOccasionRemindersChange}
                              disabled={prefsLoading || prefsSaving}
                              inputProps={{ 'aria-label': 'Email reminders for important occasions' }}
                            />
                          )}
                          label="Email reminders for important occasions"
                        />
                        {prefsSaveError && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {prefsSaveError}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={famDialogOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          closeFamDialog();
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          <Typography variant="h6">{famEditingId ? 'Edit family member' : 'Add family member'}</Typography>
          <CloseX onClick={closeFamDialog} disabled={famSaving} />
        </DialogTitle>
        <DialogContent dividers>
          {famSuccess && (
            <Alert severity="success" sx={{ mb: 1 }} onClose={() => setFamSuccess('')}>
              {famSuccess}
            </Alert>
          )}
          {famFormError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{famFormError}</Typography>}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" fullWidth size="small" value={famForm.name} onChange={(e) => setFamForm((f) => ({ ...f, name: e.target.value }))} disabled={famSaving} />
            <FormControl fullWidth size="small">
              <InputLabel>Relation</InputLabel>
              <Select
                label="Relation"
                value={famForm.relation}
                onChange={(e) => setFamForm((f) => ({ ...f, relation: e.target.value }))}
                disabled={famSaving}
              >
                {RELATION_OPTIONS.map((r) => (
                  <MenuItem key={r} value={r}>{formatRelationLabel(r)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          {famSaving && <CircularProgress size={20} sx={{ mr: 1 }} />}
          <Button variant="contained" onClick={saveFamilyMember} disabled={famSaving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={occDialogOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          closeOccDialog();
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          <Typography variant="h6">{occEditing ? 'Edit occasion' : 'Add occasion'}</Typography>
          <CloseX onClick={closeOccDialog} disabled={occSaving} />
        </DialogTitle>
        <DialogContent dividers>
          {occSuccess && (
            <Alert severity="success" sx={{ mb: 1 }} onClose={() => setOccSuccess('')}>
              {occSuccess}
            </Alert>
          )}
          {occFormError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{occFormError}</Typography>}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Family member</InputLabel>
              <Select
                label="Family member"
                value={occForm.family_member_id}
                onChange={(e) => setOccForm((f) => ({ ...f, family_member_id: e.target.value }))}
                disabled={occSaving || !!occEditing}
              >
                {familyMembers.map((m) => (
                  <MenuItem key={m.id} value={String(m.id)}>{m.name || 'Unnamed'}{isPrimaryFamilyMember(m) ? ' (Self)' : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={occForm.occasion_type}
                onChange={(e) => setOccForm((f) => ({ ...f, occasion_type: e.target.value }))}
                disabled={occSaving}
              >
                {OCCASION_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{formatOccasionTypeLabel(t, '')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {occForm.occasion_type === 'other' && (
              <TextField
                label="Custom label"
                fullWidth
                size="small"
                value={occForm.occasion_label}
                onChange={(e) => setOccForm((f) => ({ ...f, occasion_label: e.target.value }))}
                disabled={occSaving}
              />
            )}
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  label="Month"
                  value={occForm.month}
                  onChange={(e) => setOccForm((f) => ({ ...f, month: Number(e.target.value) }))}
                  disabled={occSaving}
                  renderValue={(v) => formatMonthSelectLabel(v)}
                >
                  {MONTHS.map((mo) => (
                    <MenuItem key={mo} value={mo}>{formatMonthSelectLabel(mo)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Day</InputLabel>
                <Select
                  label="Day"
                  value={occForm.day}
                  onChange={(e) => setOccForm((f) => ({ ...f, day: Number(e.target.value) }))}
                  disabled={occSaving}
                >
                  {DAYS.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          {occSaving && <CircularProgress size={20} sx={{ mr: 1 }} />}
          <Button variant="contained" onClick={saveOccasion} disabled={occSaving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          closeEditDialog();
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6">Edit profile</Typography>
          <CloseX onClick={closeEditDialog} disabled={editLoading} />
        </DialogTitle>
        <DialogContent dividers>
          {editSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEditSuccess('')}>
              {editSuccess}
            </Alert>
          )}
          {editError && (<Typography color="error" variant="body2" sx={{ mb: 1 }}>{editError}</Typography>)}
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} gutterBottom>Personal Details</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="First Name" fullWidth size="small" value={editForm.first_name} onChange={handleEditChange('first_name')} disabled={editLoading} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last Name" fullWidth size="small" value={editForm.last_name} onChange={handleEditChange('last_name')} disabled={editLoading} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email" fullWidth size="small" value={email} disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Phone" fullWidth size="small" value={editForm.phone} onChange={handleEditChange('phone')} disabled={editLoading} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Spouse" fullWidth size="small" value={editForm.spouse} onChange={handleEditChange('spouse')} disabled={editLoading} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Gothram" fullWidth size="small" value={editForm.gothram} onChange={handleEditChange('gothram')} disabled={editLoading} />
            </Grid>
          </Grid>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} gutterBottom>Address</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Address 1" fullWidth size="small" value={editForm.address_1} onChange={handleAddressChange('address_1')} disabled={editLoading} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Address 2" fullWidth size="small" value={editForm.address_2} onChange={handleAddressChange('address_2')} disabled={editLoading} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="City" fullWidth size="small" value={editForm.city} onChange={handleAddressChange('city')} disabled={editLoading} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small" disabled={editLoading}>
                <InputLabel id="state-label">State</InputLabel>
                <Select
                  labelId="state-label"
                  label="State"
                  value={editForm.state || ''}
                  onChange={handleAddressChange('state')}
                  renderValue={(val) => (US_STATES.find((s) => s.code === val)?.name || val)}
                >
                  {US_STATES.map(({ code, name: stateName }) => (
                    <MenuItem key={code} value={code}>{stateName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Postal Code" fullWidth size="small" value={editForm.postcode} onChange={handleAddressChange('postcode')} disabled={editLoading} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={editLoading}>
                <InputLabel id="country-label">Country</InputLabel>
                <Select labelId="country-label" label="Country" value={editForm.country || 'US'} onChange={handleAddressChange('country')}>
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="IN">India</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {editLoading && <CircularProgress size={20} sx={{ mr: 2 }} />}
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
